import { CommonModule } from "@angular/common";
import {
    AfterViewInit,
    Component,
    ElementRef,
    forwardRef,
    Input,
    OnDestroy,
    ViewChild,
} from "@angular/core";
import {
    ControlValueAccessor,
    NG_VALIDATORS,
    NG_VALUE_ACCESSOR,
    AbstractControl,
    ValidationErrors,
    Validator,
} from "@angular/forms";
import datepicker, { DatepickerInstance, DatepickerOptions } from "js-datepicker";
import $ from "jquery";

const DATE_FORMAT_REGEX = /^\d{1,2}\/\d{1,2}\/\d{4}$/;

function startOfToday(): Date {
    const d = new Date();
    d.setHours(0, 0, 0, 0);
    return d;
}

function parseUsDate(value: string): Date | null {
    if (!DATE_FORMAT_REGEX.test(value)) return null;

    const [m, d, y] = value.split("/").map(Number);
    const date = new Date(y, m - 1, d);
    if (
        date.getFullYear() !== y ||
        date.getMonth() !== m - 1 ||
        date.getDate() !== d
    ) {
        return null;
    }
    date.setHours(0, 0, 0, 0);
    return date;
}

function formatUsDate(date: Date): string {
    return date.toLocaleDateString("en-US");
}

@Component({
    selector: "app-date-input",
    standalone: true,
    imports: [CommonModule],
    template: `
        <input
            #input
            type="text"
            [id]="inputId"
            class="form-control"
            autocomplete="off"
            inputmode="numeric"
            maxlength="10"
            placeholder="MM/DD/YYYY"
            [disabled]="disabled"
            (keydown)="onKeyDown($event)"
            (paste)="onPaste($event)"
            (input)="onInput($event)"
            (blur)="onBlur()" />
    `,
    providers: [
        {
            provide: NG_VALUE_ACCESSOR,
            useExisting: forwardRef(() => DateInputComponent),
            multi: true,
        },
        {
            provide: NG_VALIDATORS,
            useExisting: forwardRef(() => DateInputComponent),
            multi: true,
        },
    ],
})
export class DateInputComponent
    implements ControlValueAccessor, Validator, AfterViewInit, OnDestroy {
    @ViewChild("input", { static: true }) inputRef!: ElementRef<HTMLInputElement>;

    @Input() inputId: string = "dateInput";
    @Input() minDateMode: "today" | "none" = "none";

    disabled = false;

    private _value: Date | null = null;
    // First non-null Date written via writeValue (i.e. populated from the
    // server, not user input). A past date already on the order is preserved;
    // any new date the user enters must satisfy minDate.
    private _initialValue: Date | null = null;
    private _picker: DatepickerInstance | null = null;
    private _onChange: (v: Date | null) => void = () => {};
    private _onTouched: () => void = () => {};
    private _validatorOnChange: () => void = () => {};

    ngAfterViewInit(): void {
        const input = this.inputRef.nativeElement;
        const min = this.minDate();

        const decorate = (instance: DatepickerInstance) => {
            const calendar = $(instance.calendar);
            calendar.find(".qs-arrow.qs-left").addClass("fa-regular fa-arrow-left");
            calendar.find(".qs-arrow.qs-right").addClass("fa-regular fa-arrow-right");
        };

        const options: DatepickerOptions = {
            onShow: (instance) => {
                if (min) instance.setMin(min);
                decorate(instance);
            },
            onSelect: (instance) => {
                const picked = instance.dateSelected ?? null;
                this.setFromDate(picked);
                instance.el.dispatchEvent(new Event("input", { bubbles: true }));
            },
            onMonthChange: decorate,
            formatter: (el, date) => {
                el.value = formatUsDate(date);
            },
        };

        this._picker = datepicker(input, options);
        if (min) this._picker.setMin(min);

        if (this._value) {
            input.value = formatUsDate(this._value);
            this.syncPickerDate(this._value);
        }
    }

    // js-datepicker throws if you setDate() to a day before its min. The input
    // field still shows the formatted text; we just don't pre-select the
    // disabled day on the calendar.
    private syncPickerDate(date: Date): void {
        if (!this._picker) return;
        const min = this.minDate();
        if (min && date.getTime() < min.getTime()) return;
        this._picker.setDate(date, true);
    }

    ngOnDestroy(): void {
        this._picker?.remove();
    }

    writeValue(value: Date | string | null): void {
        const next = this.coerce(value);
        this._value = next;
        if (next && !this._initialValue) {
            this._initialValue = next;
        }
        if (this.inputRef) {
            this.inputRef.nativeElement.value = next ? formatUsDate(next) : "";
            if (next) {
                this.syncPickerDate(next);
            }
        }
    }

    registerOnChange(fn: (v: Date | null) => void): void {
        this._onChange = fn;
    }

    registerOnTouched(fn: () => void): void {
        this._onTouched = fn;
    }

    setDisabledState(isDisabled: boolean): void {
        this.disabled = isDisabled;
    }

    validate(_control: AbstractControl): ValidationErrors | null {
        const raw = this.inputRef?.nativeElement.value?.trim();
        if (!raw) return null;

        const parsed = parseUsDate(raw);
        if (!parsed) return { invalidDate: true };

        const min = this.minDate();
        if (min && parsed.getTime() < min.getTime() && !this.matchesInitial(parsed)) {
            return { dateBeforeMin: true };
        }
        return null;
    }

    private matchesInitial(date: Date): boolean {
        const initial = this._initialValue;
        if (!initial) return false;
        return (
            initial.getFullYear() === date.getFullYear() &&
            initial.getMonth() === date.getMonth() &&
            initial.getDate() === date.getDate()
        );
    }

    registerOnValidatorChange(fn: () => void): void {
        this._validatorOnChange = fn;
    }

    onKeyDown(event: KeyboardEvent): void {
        const navKeys = [
            "Backspace",
            "Delete",
            "ArrowLeft",
            "ArrowRight",
            "ArrowUp",
            "ArrowDown",
            "Tab",
            "Home",
            "End",
            "Enter",
            "Escape",
        ];
        if (event.ctrlKey || event.metaKey || navKeys.includes(event.key)) return;

        if (event.key.length === 1 && !/[0-9/]/.test(event.key)) {
            event.preventDefault();
        }
    }

    onPaste(event: ClipboardEvent): void {
        const text = event.clipboardData?.getData("text") ?? "";
        if (!/^[0-9/]+$/.test(text)) {
            event.preventDefault();
        }
    }

    onInput(event: Event): void {
        const value = (event.target as HTMLInputElement).value;
        const parsed = value ? parseUsDate(value) : null;

        if (parsed) {
            this._value = parsed;
            this._onChange(parsed);
        } else if (!value) {
            this._value = null;
            this._onChange(null);
        } else {
            this._onChange(null);
        }
        this._validatorOnChange();
    }

    onBlur(): void {
        this._onTouched();
        this._validatorOnChange();
    }

    private setFromDate(date: Date | null): void {
        this._value = date;
        if (this.inputRef) {
            this.inputRef.nativeElement.value = date ? formatUsDate(date) : "";
        }
        this._onChange(date);
        this._validatorOnChange();
    }

    private coerce(value: Date | string | null | undefined): Date | null {
        if (!value) return null;
        if (value instanceof Date) return Number.isNaN(value.getTime()) ? null : value;
        if (typeof value === "string") {
            const parsed = parseUsDate(value);
            if (parsed) return parsed;

            const direct = new Date(value);
            return Number.isNaN(direct.getTime()) ? null : direct;
        }
        return null;
    }

    private minDate(): Date | null {
        return this.minDateMode === "today" ? startOfToday() : null;
    }
}
